import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as orderApi from '../api/orderApi';

export const createOrder = createAsyncThunk(
  'orders/create',
  async (orderData, { rejectWithValue }) => {
    try {
      const data = await orderApi.createOrder(orderData);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create order');
    }
  }
);

export const fetchMyOrders = createAsyncThunk(
  'orders/fetchMy',
  async (params, { rejectWithValue }) => {
    try {
      const data = await orderApi.fetchMyOrders(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchById',
  async (orderId, { rejectWithValue }) => {
    try {
      const data = await orderApi.fetchOrderById(orderId);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Order not found');
    }
  }
);

export const trackOrder = createAsyncThunk(
  'orders/track',
  async (orderId, { rejectWithValue }) => {
    try {
      const data = await orderApi.trackOrder(orderId);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Tracking failed');
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancel',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const data = await orderApi.cancelOrder(orderId, reason);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to cancel order');
    }
  }
);

export const requestReturn = createAsyncThunk(
  'orders/return',
  async ({ orderId, returnData }, { rejectWithValue }) => {
    try {
      const data = await orderApi.requestReturn(orderId, returnData);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to request return');
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    orders: [],
    currentOrder: null,
    trackingInfo: null,
    loading: false,
    error: null,
    pagination: {
      page: 1,
      pages: 1,
      total: 0,
    },
  },
  reducers: {
    clearOrderError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.trackingInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload.order || action.payload.data || action.payload;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders || action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload.order || action.payload.data || action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(trackOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.trackingInfo = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.trackingInfo = action.payload.tracking || action.payload.data || action.payload;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.trackingInfo = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const updated = action.payload.order || action.payload;
        if (state.currentOrder && state.currentOrder._id === updated._id) {
          state.currentOrder = updated;
        }
        state.orders = state.orders.map((o) => (o._id === updated._id ? updated : o));
      })
      .addCase(requestReturn.fulfilled, (state, action) => {
        const updated = action.payload.order || action.payload;
        if (state.currentOrder && state.currentOrder._id === updated._id) {
          state.currentOrder = updated;
        }
      });
  },
});

export const { clearOrderError, clearCurrentOrder } = orderSlice.actions;
export default orderSlice.reducer;
